import React from 'react';
import { Box, Container, Typography, Grid, Card } from '@mui/material';
import SectionHeading from './SectionHeading';
import { marketingCardSx, marketingPalette, marketingSectionSx } from './marketingSystem';

const problems = [
  {
    accent: '#ef4444',
    eyebrow: 'Live conditions',
    stat: '36pt',
    statLabel: 'approval rate spread',
    statContext: 'Same corridor · same PSP · one trading day',
    title: 'Your rule fires the same way at 47% approval and at 83%.',
    description:
      'Live approval rate spreads, issuer behavior shifts, and route-level performance change constantly. Static routing configuration can\'t read live network signals — so it can\'t act on them.',
  },
  {
    accent: '#0f6fff',
    eyebrow: 'Missing context',
    stat: '0',
    statLabel: 'of your priorities in the rule',
    statContext: 'Volume discounts, chargeback exposure, MID economics',
    title: 'Your tier position, chargeback profile, and MID economics aren\'t in the rule. Not ever.',
    description:
      'Dashboards report what happened after settlement. Orchestrators execute the route you configured. Neither returns the right decision for the live payment moment — with your actual business priorities in view.',
  },
  {
    accent: '#14b8a6',
    eyebrow: 'Configuration drift',
    stat: '12+',
    statLabel: 'months since last change',
    statContext: 'Average across payment teams',
    title: 'The network has changed. Issuer behavior has changed. Your configuration hasn\'t.',
    description:
      'Interchange categories, route performance, and network conditions shift continuously. The rule set that made sense when it was built is now running a payment landscape it was never configured for. Ormal re-authors the strategy when your book changes — the rule set that drifts for 12 months is the thing it replaces.',
  },
];

const ProblemSection: React.FC = () => {
  return (
    <Box component="section" sx={{ ...marketingSectionSx, pt: { xs: 5, md: 6 }, pb: { xs: 5, md: 7 } }}>
      <Container maxWidth="lg">
        <SectionHeading
          eyebrow="Why payment teams lose performance"
          title="Your stack processes every transaction the same way. Your network doesn't."
          description="Business priorities, risk profiles, and network conditions are different on every transaction. Static routing can't see that. Neither can last month's dashboard."
          align="center"
          marginBottom={4}
        />
        <Grid container spacing={2.5}>
          {problems.map((item, i) => (
            <Grid item xs={12} md={4} key={i}>
              <Card
                sx={{
                  ...marketingCardSx,
                  borderTop: `3px solid ${item.accent}`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0,
                }}
              >
                {/* Stat callout */}
                <Box
                  sx={{
                    pb: 1.8,
                    mb: 1.8,
                    borderBottom: '1px solid',
                    borderColor: 'rgba(196,212,232,0.6)',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'var(--font-display)',
                      fontSize: '2.2rem',
                      fontWeight: 300,
                      color: item.accent,
                      letterSpacing: '-0.04em',
                      lineHeight: 1,
                    }}
                  >
                    {item.stat}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.78rem',
                      fontWeight: 500,
                      color: marketingPalette.headline,
                      mt: 0.3,
                      lineHeight: 1.3,
                    }}
                  >
                    {item.statLabel}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.68rem',
                      color: marketingPalette.muted,
                      mt: 0.2,
                      lineHeight: 1.3,
                    }}
                  >
                    {item.statContext}
                  </Typography>
                </Box>

                {/* Label */}
                <Typography
                  sx={{
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: item.accent,
                    mb: 0.75,
                  }}
                >
                  {item.eyebrow}
                </Typography>

                {/* Title */}
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: '0.97rem',
                    color: marketingPalette.headline,
                    lineHeight: 1.42,
                    mb: 1,
                  }}
                >
                  {item.title}
                </Typography>

                {/* Description */}
                <Typography
                  sx={{
                    color: marketingPalette.body,
                    fontSize: '0.88rem',
                    lineHeight: 1.7,
                  }}
                >
                  {item.description}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default ProblemSection;
